import React, { useMemo, useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid,  
	RCCTooltip, 
	RCCInput,
	RCCServicesApi,
	RCCHookToast,
	RCCAlert,
	RCCSelectOptionsCard
} from "react-common-components";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import { useAuth } from "src/context/Auth/context";
import Select from "src/components/Select";
import LovUsers from "src/components/Lov/Users";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import { format } from "date-fns";
import { standardErrorMessages } from "src/utils/errors";
import Loader from "src/components/Loader";
import { getLocalStorage, setLocalStorage } from "src/utils/localStorage";
import FiltersTagsContainer from "src/components/FiltersTagsContainer";
import { IRefFiltersTagsContainer, TagsTypes } from "src/components/FiltersTagsContainer/interface";
import ButtonExpandGroupers from "src/components/ButtonExpandGroupers";
import { getCookie, setCookie } from "src/utils/cookies";
import LovRiskClassification from "src/components/Lov/RiskClassification";
import LovProjects from "src/components/Lov/Projects";
import LovGrouperRisks from "src/components/Lov/GrouperRisks";
import LovProcess from "src/components/Lov/Process";
import AppIcons from "src/components/AppIcons";
import Reports from "src/components/Reports";
import ReportGroupers from "src/components/Reports/interface";
import { useLocation, useParams } from "react-router-dom";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import { PermissionEnum } from "src/context/Auth/interface";
import returnValueForSelect from "src/utils/returnValueForSelect";
import LovSectors from "src/components/Lov/Sectors";
import ScreenStandardGroup from "src/Interfaces/screenStandardGroup";
import { CheckImpact, CheckProbability, CheckQuadrant, RisksSituation, IFilters, IRefIRisksModals, GroupersRisks, IGroupersRisks, IGroupersSelect } from "./interface";
import Modals from "./modals";
import GrouperOfRisks from "./components/grouperOfRisks";

const RisksPanel: React.FC = () => { 

	const params = useParams<{
		riskId: string;
	}>();

	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const {administrator, superUser, checkIfHasSpecificPermission} = useAuth();
	const hasPrivileges = administrator || superUser;
	const { addToast } = RCCHookToast();
	const { translate } = useTranslation();
	const { user } = useAuth();
	const refModals = useRef<IRefIRisksModals>(null);
	const [myRisks, setMyRisks] = useState(false);

	const [defaultGrouper, setDefaultGrouper] = useState<GroupersRisks | undefined>(getCookie(ScreenStandardGroup.RISKS) as GroupersRisks);

	const [layoutList, setLayoutList] = useState<"card" | "list">(getLocalStorage(`RisksScreenLayoutList-${user.efa005Id}`) === "card" ? "card" : "list");
	const [showFilters, setShowFilters] = useState(false);
	const [showTagsFilters, setShowTagsFilters] = useState(false);
	const filtersTagsRef = useRef<IRefFiltersTagsContainer>(null);
	const [filters, setFilters] = useState<IFilters>({ 
		risk: params.riskId || "",
		filter: "", 
		active: objQueryParams?.active || "T", 
		dateStart: "",
		dateEnd : "", 
		probability: objQueryParams?.probability || "",
		impact:  objQueryParams?.impact || "",
		tags: "",
		classification: objQueryParams?.classification || "",
		situation: objQueryParams?.situation || undefined,
		quadrant: objQueryParams?.quadrant || "",
		riskSector: "",
		impactSector: ""
	});
	
	const {
		handleSubmit : handleSubmitFilters, 
		control:controlFilters, 
		register : registerFilters, 
		reset:resetFilters, 
		setValue: setValueFilters, 
		watch: watchFilters 
	} = useForm();

	const buttonSubmitFormGroupersRef = useRef<HTMLButtonElement>(null);

	const { handleSubmit : handleSubmitGroupers, control: controlGroupers } = useForm();
	const [groupers, setGroupers] = useState<IGroupersRisks[]>([]);

	const [groupersSelect, setGroupersSelect] = useState<IGroupersSelect>({
		group: objQueryParams?.group as GroupersRisks || defaultGrouper || GroupersRisks.RISK_CLASSIFICATION
	});

	const submitGroupers = async (data: any) => {
		setGroupersSelect({
			...data
		});
	};

	
	const objToValue = (obj: any) => {
		if(typeof obj === "object")
			return obj.value;
		if(typeof obj === "string")
			return parseInt(obj, 10) ;
		return obj;
	};

	const { errorFetching, loading, loadData } = useEffectFetchData<IGroupersRisks[]> (
		"Risks/ListGroupers", 
	  { 
			...groupersSelect,
			risk: filters.risk ? filters.risk : "",
			filter: filters.filter ? filters.filter : "",
			quadrant: filters.quadrant ? filters.quadrant : "",
			classification: filters.classification ? filters.classification : "",
			ria001Id: filters.ria001Id ? filters.ria001Id : "",
			situation: filters.situation && objToValue(filters.situation.toString()) !== 4  ? filters.situation : "",
			probability: filters.probability ? filters.probability : "",
			impact: filters.impact ? filters.impact : "",
			responsable: filters.responsable ? filters.responsable: "",
			process: filters.process ? filters.process : "",
			projects: filters.projects ? filters.projects : "",
			tags: filters.tags ? filters.tags : "",
			dateStart : filters.dateStart ? filters.dateStart : "",
			dateEnd: filters.dateEnd ? filters.dateEnd : "",
			riskSector: filters.riskSector ? filters.riskSector : "",
			impactSector: filters.impactSector ? filters.impactSector : ""
		 },
		 ({ data }) => {
			 setGroupers(data);
		 },
		 [groupersSelect, filters],
		 true
	);
	
	const submitFilters = (data: any) => {
		setFilters(() => {	
			const newState = {} as any;
			newState.filter = data.filter;
			newState.quadrant = data.quadrant?.value || data.quadrant || "";
			newState.ria001Id =  data.ria001Id ? data.ria001Id : "";
			newState.situation =  data.situation && objToValue(data.situation) !== 4 ? objToValue(data.situation) : "";
			newState.probability = data.probability?.value || data.probability || "";
			newState.impact= data.impact?.value || data.impact || "";
			newState.process = data.process?.value || data.process || "";
			newState.projects = data.projects?.value || data.projects || "";
			newState.tags = data.tags ? data.tags : "";
			newState.responsable = data.responsable?.value || data.responsable || "";
			newState.classification = data.classification?.value || data.classification || "";
			newState.responsable = myRisks ? user.efa005Id : newState.responsable;
			newState.riskSector = data.riskSector?.value || data.riskSector || "";
			newState.impactSector = data.impactSector?.value || data.impactSector || "";

			if(data.dateStart)
				newState.dateStart = format(data.dateStart,"dd/MM/yyyy");
			if(data.dateEnd)
				newState.dateEnd = format(data.dateEnd,"dd/MM/yyyy");

			return newState;
		});	
		
	};
	
	const submitFiltersTags = async () => {
		setFilters((prevState) => {
			const updateState = {...prevState};
			updateState.tags = filtersTagsRef.current?.response ? filtersTagsRef.current?.response : "";
			return updateState;
		});
	};


	const onCallbackAdd = () => {
		loadData();
	};


	const setAsDefaultGrouper = async () => {
		const {group} = groupersSelect;
		try{
			await RCCServicesApi.postRequest({
				method: "Risks/SetDefaultGrouper",
				body: {
					group
				}
			});
			setDefaultGrouper(group);
			setCookie(ScreenStandardGroup.RISKS, group);
			addToast({
				color: "green",
				description: translate("Agrupador padrão atualizado com sucesso"),
				title: translate("Sucesso")
			});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao alterar agrupador padrão"), addToast);
		}

	};
	
	const Groupers = useMemo(() => (
		<>
			{loading ? (
				<Loader />
			) : (
				<>
					{errorFetching ? (
						<RCCAlert 
							title={translate("Atenção")}
							description={translate("Erro ao tentar encontrar resultados para esta pesquisa")}
						/>
					): (
						<>
							{groupers.length === 0 ? (
								<RCCAlert 
									title={translate("Atenção")}
									description={translate("Nenhum agrupador encontrado")}
								/>
							): (
								<>
									{groupers.map((grouper) => (
										<GrouperOfRisks 
											grouper={grouper} 
											filters={filters} 
											layoutList={layoutList} 
											groupersSelect={groupersSelect} 
											key={`${grouper.id}-grouper`}
										/>
									))}
								</>
							)}
						</>
					)}
				</>
			)}	
		</>
	), [groupers,layoutList, loading, errorFetching]);

	const situationOptions = [
		{
			value: RisksSituation.OPEN.toString(),
			label: translate("Aberto")
		},{
			value: RisksSituation.FINALY.toString(),
			label: translate("Finalizado")
		},
		{
			value: RisksSituation.CANCELED.toString(),
			label: translate("Cancelado")
		},
		{
			value: RisksSituation.ALL.toString(),
			label: translate("Todos")
		}
	];

	const quadrantOptions = [
		{
			value: CheckQuadrant.THREAT.toString(),
			label: translate("Ameaça")
		},
		{
			value: CheckQuadrant.OPPORTUNITY.toString(),
			label: translate("Oportunidade")
		}
	];

	const probabilityOptions = [
		{
			value: CheckProbability.VERY_HIGH.toString(),
			label: translate("Muito alto")
		},{
			value: CheckProbability.HIGH.toString(),
			label: translate("Alto")
		},
		{
			value: CheckProbability.MODERATE.toString(),
			label: translate("Moderado")
		},
		{
			value: CheckProbability.LOW.toString(),
			label: translate("Baixo")
		},{
			value: CheckProbability.VERY_LOW.toString(),
			label: translate("Muito baixo")
		}
	];

	const impactOptions = [
		{
			value: CheckImpact.VERY_HIGH.toString(),
			label: translate("Muito alto")
		},{
			value: CheckImpact.HIGH.toString(),
			label: translate("Alto")
		},
		{
			value: CheckImpact.MODERATE.toString(),
			label: translate("Moderado")
		},
		{
			value: CheckImpact.LOW.toString(),
			label: translate("Baixo")
		},{
			value: CheckImpact.VERY_LOW.toString(),
			label: translate("Muito baixo")
		}
	];

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Painel de Riscos")}
					icon={() => <AppIcons name="risks" />}	
				>
					{(hasPrivileges || checkIfHasSpecificPermission("/risks/panel", PermissionEnum.CREATE_UPDATE)) &&
						<>
							<RCCTooltip
								text={translate("Novo risco")}
								color="purple"
							>
								<RCCButton
									icon={{name: "plus", color: "#FFF", size: 18}}
									color="purple"
									layout="isRounded"
									onClick={() => refModals.current?.actionButtonAdd(onCallbackAdd)}
									size="md"
								/>
							</RCCTooltip>
						</>
					}
					<RCCTooltip
						text={layoutList === "card" ? translate("Alterar para lista") : translate("Alterar para cards")}
						color="grey"
					>
						<RCCButton
							icon={{
								name: layoutList === "card" ? "list" : "table", 
								color: "#FFF", 
								size: 16
							}}
							color="grey"
							layout="isRounded"
							size="md"
							onClick={() => {
								if(layoutList === "card"){
									setLayoutList("list");
									setLocalStorage(`RisksScreenLayoutList-${user.efa005Id}`, "list");
									return;
								}
								setLocalStorage(`RisksScreenLayoutList-${user.efa005Id}`, "card");
								setLayoutList("card");
							}}
						/>
					</RCCTooltip>
					<Reports grouper={ReportGroupers.RISKS}/>
					<RCCTooltip
						text={`${translate("Filtros")} Tags`}
						color={showTagsFilters || filters.tags ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "tags", color: "#FFF", size: 16}}
							color={showTagsFilters || filters.tags ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowTagsFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Filtros")}
						color={
							showFilters || 
							filters.filter || 
							filters.dateStart  ||
							filters.dateEnd ||
							filters.probability ||
							filters.impact || 
							filters.ria001Id || 
							(filters.situation && filters.situation !== RisksSituation.ALL.toString()) ||
							filters.process ||
							filters.projects || 
							filters.responsable ||
							filters.classification ||
							filters.quadrant ||
							filters.riskSector ||
							filters.impactSector
								? "blue" : "grey" 
						}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={
								showFilters ||
								filters.filter ||
								filters.dateStart  ||
								filters.dateEnd ||
								filters.probability ||
								filters.impact || 
								filters.ria001Id ||
								filters.situation || 
								filters.process ||
								filters.projects || 
								filters.responsable ||
								filters.classification ||
								filters.quadrant ||
								filters.riskSector ||
								filters.impactSector
									? "blue" : "grey" 
							}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
					<ButtonExpandGroupers />	
				</PageTitle>		

				{/* Filters */}			
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ 
						filter: "", 
						dateStart: null,
						dateEnd : null, 
						probability: "",
						impact: "", 
						ria001Id: "", 
						situation: "", 
						process: "", 
						projects: "", 
						responsable: myRisks ? user.efa005Id: "",
						classification: "",
						quadrant: "",
						riskSector: "",
						impactSector: ""
					})}
					showFilters={showFilters}
				>
					<RCCGrid
						container
						spacing={1}
						styleInline="margin-bottom: 2.4rem !important;"
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<DateTimePickerRange
								control={controlFilters}
								nameInit = "dateStart"
								nameEnd = "dateEnd"
								label = {translate("Data")}
								labelSeparator = {translate("até")}
								type = "datePicker"
								defaultValueInit ={filters.dateStart}
								defaultValueEnd ={filters.dateEnd}
								setValue={setValueFilters}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<Select 
								control={controlFilters}
								name="quadrant"
								options={quadrantOptions}
								defaultValue={returnValueForSelect(filters.quadrant, quadrantOptions)}
								label={translate("Tipo")}
								submit="value and label"
								isClearable
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<Select 
								control={controlFilters}
								name="probability" 
								options={probabilityOptions} 
								label={translate("Probabilidade")}
								defaultValue={returnValueForSelect(filters.probability, probabilityOptions)}
								submit="value and label"
								isClearable
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<Select 
								control={controlFilters}
								name="impact" 
								options={impactOptions} 
								label={translate("Impacto")}
								defaultValue={returnValueForSelect(filters.impact, impactOptions)}
								submit="value and label"
								isClearable	
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item	
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovUsers 
								control={controlFilters}
								name="responsable"
								label={translate("Responsável")}
								defaultValue={watchFilters("filters.responsable")  || ""}
								disabled={myRisks}
								submit="value and label"
								isClearable
							/>	
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<LovRiskClassification 
								name="classification"
								control={controlFilters}
								label={translate("Classificação")}
								defaultValue={filters.classification || ""}
								submit="value and label"
								isClearable
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<LovProcess
								control={controlFilters}
								name="process" 
								label={translate("Processo")}
								defaultValue={filters.process || ""}
								submit="value and label"
								isClearable
								isCreatable={false}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<Select 
								control={controlFilters}
								name="situation" 
								options={situationOptions} 
								label={translate("Situação")}
								defaultValue={filters.situation ? returnValueForSelect(filters.situation, situationOptions) : {
									value: RisksSituation.ALL.toString(),
									label: translate("Todos")
								}}
								submit="value and label"
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<LovProjects 
								name="projects"
								control={controlFilters}
								label={translate("Projetos")}	
								defaultValue={filters.projects || ""}
								submit="value and label"
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovSectors 
								control={controlFilters}
								name="riskSector"
								label={translate("Setor do risco")}
								defaultValue={filters.riskSector || ""}
								submit="value and label"
								isClearable
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovSectors 
								control={controlFilters}
								name="impactSector"
								label={translate("Setor do impacto")}
								defaultValue={filters.impactSector || ""}
								submit="value and label"
								isClearable
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="filter" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={filters.filter}
							/>
						</RCCGrid>
					</RCCGrid>
						
				</FiltersContainer>

				{/* Filters Tags */}
				<FiltersTagsContainer 
					ref={filtersTagsRef}
					onSubmitFilters={submitFiltersTags}
					tagType={TagsTypes.RISKS}
					showFilters={showTagsFilters}
				/>

				<form onSubmit={handleSubmitGroupers(submitGroupers)} style={{marginBottom: "1rem", height: "60px"}}>
					<RCCGrid
						container
						item
						spacing={1}
						justifyContent="space-between"
					>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={4}
							alignItems="baseline"
						>
							<LovGrouperRisks 
								control={controlGroupers}
								name="group"
								onChangeCapture={() => buttonSubmitFormGroupersRef.current?.click()}
								defaultValue={defaultGrouper || "1"}
								styleInline="width: auto; flex: 1;"
							/>
							<RCCTooltip
								color="grey"
								text={translate("Definir como agrupador padrão")}
							>
								<RCCButton 
									color="transparent"
									icon={{
										name: "checkBookmark",
										color: "var(--grey)"
									}}
									onClick={setAsDefaultGrouper}
								/>
							</RCCTooltip>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={6}
							lg={3}
							justifyContent="flex-end"
						>
							<RCCSelectOptionsCard
								defaultValue={myRisks ? "S" : "N"}
								options={[{
									value: "N",
									label: translate("Todos riscos")
								},{
									value: "S",
									label: translate("Meus riscos")
								}]}
								label="&nbsp;"
								onChange={(e) => {
									setValueFilters("responsable", "");
									if(e === "S"){
										setMyRisks(true);
										setFilters({
											...filters,
											responsable: user.efa005Id
										});
										return;
									}
									setMyRisks(false);
									setFilters({
										...filters,
										responsable: ""
									});
								}}
							/>
						</RCCGrid>
					</RCCGrid>
					
					<button 
						aria-label="Submit Form"
						ref={buttonSubmitFormGroupersRef}
						type="submit" 
						style={{ display: "none" }}
					></button>
				</form>
						
				{/* List */}	
				{Groupers}
				
			</RCCGrid>

			<Modals ref={refModals} />
		</>
	);
};

export default RisksPanel;

